// api
import api from '../utils/api';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import useFlashMessage from './useFlashMessage';

export default function useAuth(){
    const [ authenticated, setAuthenticated ] = useState(false);
    const [user, setUser] = useState(null);
    const { setFlashMessage } = useFlashMessage();
    const navigate = useNavigate();

    async function register(user){
        let msgText = 'Cadastro realizado com sucesso!';
        let msgType = 'success';

        try {
            const data = await api.post('/users/register', user).then((response) => {
                return response.data;
            });
            await authUser(data);
        } catch (error) {
            msgText = error.response?.data?.message || "Erro ao cadastrar";
            msgType = 'error';
        }

        setFlashMessage(msgText, msgType);
    }

    async function login(user){
        let msgText = "Login realizado com sucesso!";
        let msgType = "success";

        try {
            const data = await api.post('/users/login', user).then((response) => {
                return response.data;
            });
            await authUser(data);
        } catch (error) {
            msgText = error.response?.data?.message || "Erro ao fazer login";
            msgType = "error";
        }

        setFlashMessage(msgText, msgType);
    }

    /*
        authUser: só autentica e carrega os dados do usuário.
        NÃO navega mais sozinho — cada tela (Register, Login) decide
        pra onde ir depois de chamar register()/login(), porque
        algumas telas precisam mostrar um passo extra antes (ex: o
        checkout do plano pago) e um redirect automático aqui
        atropelava esse fluxo.
    */
    async function authUser(data){
        setAuthenticated(true);

        if(data?.token){
            localStorage.setItem("token", JSON.stringify(data.token));
            api.defaults.headers.Authorization = `Bearer ${data.token}`;
        }

        await fetchUser();
    }

    function logout(){
        setAuthenticated(false);
        setUser(null);
        localStorage.removeItem('token');
        api.defaults.headers.Authorization = undefined;
        navigate('/login');
        setFlashMessage('Logout realizado com sucesso!', 'success');
    }

    async function fetchUser(){
        try {
            const response = await api.get('/users/checkuser');
            setUser(response.data);
        } catch (error) {
            setUser(null);
        }
    }

    async function editUser(updatedUser, preview){
        let msgType = 'success';
        const token = localStorage.getItem('token');
        if (!token){
            setFlashMessage('Usuário não autenticado', 'error');
            return;
        }

        const formData = new FormData();

        formData.append('name', updatedUser.name);
        formData.append('email', updatedUser.email);
        formData.append('phone', updatedUser.phone || '');
        formData.append('cnpj', updatedUser.cnpj || '');
        if (updatedUser.password) {
            formData.append('password', updatedUser.password);
            formData.append('confirmpassword', updatedUser.confirmpassword);
        }
        formData.append('localization', JSON.stringify(updatedUser.localization || {}));
        if(preview){
            formData.append('image', preview);
        }

        try {
            const { data } = await api.patch(`/users/edit/${user._id}`, formData, {
                headers: {
                    Authorization: `Bearer ${JSON.parse(token)}`,
                    'Content-Type': 'multipart/form-data'
                }
            });

            setUser(prev => ({
                ...prev,
                ...data.user
            }));

            setFlashMessage(data.message, msgType);
            return data.user;
        } catch (error) {
            msgType = 'error';
            setFlashMessage(error.response?.data?.message || 'Erro ao atualizar usuário', msgType);
            return null;
        }
    }

    /* ─── changePlan: free (PATCH) ou pago (Mercado Pago) ─── */
    async function changePlan(token, planId) {
        try {
            if (planId === 'free') {
                await api.patch('/users/plan', { plan: 'free'}, {
                    headers: { Authorization: `Bearer ${token}`},
                });
                setFlashMessage("Plano alterado para Gratuito.", "success");
                await fetchUser();
                return { error: false };
            }
            // Nota: essa rota (/payments/create-preference) é do fluxo antigo
            // de pagamento avulso (Preference), não do fluxo de assinatura
            // recorrente atual (que usa o Card Payment Brick + /subscriptions/create
            // via useSubscription.js). Se essa função ainda for usada em algum
            // lugar, considere trocar pra usar useSubscription().subscribe() em vez disso.
            const response = await api.post('payments/create-preference', { plan: planId }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const { init_point } = response.data;
            window.location.href = init_point;
        } catch (error) {
            setFlashMessage("Erro ao alterar plano. Tente novamente.", "error");
            return { error: true };
        }
    }

    async function registerPdf() {
        const token = localStorage.getItem('token');
        if (!token) {
            setFlashMessage('Usuário não autenticado', 'error');
            return { error: true };
        }

        try {
            const { data } = await api.patch('/users/register-pdf', {}, {
                headers: { Authorization: `Bearer ${JSON.parse(token)}` }
            });

            setUser(prev => ({ ...prev, pdfsGeneratedThisWeek: data.pdfsGeneratedThisWeek }));
            return { success: true };
        } catch (error) {
            const message = error.response?.data?.message || 'Erro ao registrar PDF';
            if (error.response?.data?.limitReached) {
                return { limitReached: true, message };
            }
            setFlashMessage(message, 'error');
            return { error: true, message };
        }
    }

    useEffect(() => {
        const token = localStorage.getItem("token");

        if (token) {
            api.defaults.headers.Authorization = `Bearer ${JSON.parse(token)}`;
            setAuthenticated(true);
            fetchUser();
            // Na tela de checkout usada pelo WebView do app mobile, o token é
            // gravado propositalmente por essa própria tela (via query param) —
            // nesse caso não queremos redirecionar, senão sai da tela de pagamento.
            if (window.location.pathname !== '/mobile-checkout') {
                navigate('/dashboard'); // mantido: só roda 1x, ao carregar o app com token salvo
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    return { authenticated, user, setUser, register, login, logout, fetchUser, editUser, changePlan, registerPdf, authUser };
}